import axios from "axios";

import { config } from "../../config";
import { LocalStorage } from "../../data/localstorage";
import moment from "moment";

import { EVCStation, EVCStationHoliday, EVCStationImage } from '../../entity/location/evcstation';
import { EVCStationStatus, EVCStationVisibiltyStatus, EVCStationThirdPartyStatus, EVCStationThirdPartyList } from "../../entity/location/evcstation";
import { HoursOfOperation, Time } from '../../entity/hours-of-operation';
import { Coordiate } from '@/entity/location/corodinate';
import { User } from '@/entity/authentication/user';
import { EVCharger, EVChargerType, EVChargerOutputType, EVChargerOutputPortType } from '@/entity/station/evcharger';
import { EVChargerTransformer } from '../transformers/evcharger';
import { RecentlySelectedFilterCriteria } from '@/entity/station/reservation';
import { QueryInfo } from '@/util';
import { EVCStationBaySetup, EVCStationBayStatus, EVCBayOutletGun, EVCBayOutlet, EVCBayOutletTariff, BayDetailsForReservation } from '@/entity/station/evcbay';
import { StationTransformer } from '../transformers/evcstation';
import { StateTransformer } from '../transformers/address';
import { EVCStationBaySetupTransformer } from '../transformers/evcstation-bay-setup';
import { EVCBayOutletGunTransformer } from '../transformers/evcstation-bay-outlet-gun';
import { Rate } from '@/entity/rate';
import { setup, Result } from '..';
import { UOM } from '@/entity/uom';
import { EVChargerOutputTransformer } from '../transformers/evcharger-output';
import { EVChargerOutputPortTransformer } from '../transformers/evcharger-ouput-port';
import { EVChargeTariff } from '@/entity/station/evcharge-tariff';

export interface StationWithChargerQuery {
    chargerType?: EVChargerType
    outputType?: EVChargerOutputType
    portType?: EVChargerOutputPortType
    radius?: number
    viewType?: string
}

export interface StationQuery {
    id?: string
    name?: string
    city?: string
    effectiveBetween?: {
        from?: Date,
        to?: Date
    },
    installationsBetween?: {
        min?: number,
        max?: number
    }
    status?: EVCStationStatus
}

export interface BayQuery {
    station?: EVCStation
    charger?: EVCharger
    chargerType?: EVChargerType
    effectiveBetween?: {
        from?: Date,
        to?: Date
    },
    status?: EVCStationBayStatus
}

export class EVCStationService {
    async stationsAtPageByPaginatedWithSizeMatchingQuery(page: number, size: number, query?: StationQuery): Promise<Result<EVCStation>> {
        let queryInfo: any = {
            page: page,
            "page-size": size,
            stationName: name
        }  
        if(query) {
            queryInfo = {
                ...queryInfo,
                stationId: query.id,
                stationName: query.name,
                city: query.city,
            }
            if(query.effectiveBetween) {
                if(query.effectiveBetween.from) {
                    queryInfo = { ...queryInfo,
                        effectiveFrom: moment(query.effectiveBetween.from).utc().format('YYYY-MM-DD'),
                    }
                }
                if(query.effectiveBetween.to) {
                    queryInfo = { ...queryInfo,
                        effectiveTo: moment(query.effectiveBetween.to).utc().format('YYYY-MM-DD')
                    }
                }
            }
            if(query.installationsBetween) {
                if(query.installationsBetween.min) {
                    queryInfo = { ...queryInfo,
                        min: query.installationsBetween.min
                    }
                }
                if(query.installationsBetween.max) {
                    queryInfo = { ...queryInfo,
                        max: query.installationsBetween.max
                    }
                }
            }
            if(query.status) {
                queryInfo = { 
                    ...queryInfo, 
                    status: {
                        code: query.status.id
                    }
                }
            }
        }        
        let response = await axios.get(config.api.host + "/stations/search" + new QueryInfo(queryInfo).toURLString(), {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let evcStationsInfos: any[] = response.data.data
        let evcStations: EVCStation[] = []
        evcStationsInfos.forEach(evcStationInfo => {                       
            evcStations.push(new StationTransformer().transform(evcStationInfo))
        }); 
        
        return Promise.resolve(new Result<EVCStation>(response.data.meta.totalrecords, evcStations))
    }

    async stationsAroundLocationForUser(coordinate: Coordiate, user: User, query?: StationWithChargerQuery): Promise<EVCStation[]> {
        let queryInfo: any = {
            userId: user.id,
            page: 1,
            "page-size": 10000000,
            latitude: coordinate.latitude,
            longitude: coordinate.longitude,
            radius: query ? query.radius : 5,
            viewType: 'MAP'
        }
        if(query) {
            if(query.chargerType) {
                queryInfo = {...queryInfo,
                    chargerType: {
                        code: query.chargerType.id
                    }
                }
            }
            if(query.outputType) {
                queryInfo = {...queryInfo,
                    outputType: {
                        code: query.outputType.id
                    }
                }
            }
            if(query.portType) {
                queryInfo = {...queryInfo,
                    portType: {
                        code: query.portType.id
                    }
                }
            }
            if(query.viewType) {
                queryInfo = {...queryInfo,
                    viewType: query.viewType
                }
            }
        }
        let response = await axios.get(config.api.host + "/accounts/"+ user.id + "/reservations/stations/search" + new QueryInfo(queryInfo).toURLString(), {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let evcStationsInfos: any[] = response.data.data;
        let evcStations: EVCStation[] = []
        evcStationsInfos.forEach(evcStationInfo => {
            evcStations.push(new StationTransformer().transform(evcStationInfo))
            
        });
        return Promise.resolve(evcStations)
    }

    async stationWithId(id: number): Promise<EVCStation> {
        let response = await axios.get(config.api.host + "/stations/" + id, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let stationInfo: any = response.data.data
        let station = new StationTransformer().transform(stationInfo)
        return Promise.resolve(station)
    }

    // async gunsOfChargersForSelectedStationByUser(station: EVCStation, user: User): Promise<EVCBayOutletGun[]> {
        async gunsOfChargersForSelectedStationByUser(station: EVCStation, user: User) {
        let queryInfo = {
            userId: user.id
        }
        let response = await axios.get(config.api.host + "/accounts/"+ user.id + "/reservations/stations/" + station.id + "/chargers" + new QueryInfo(queryInfo).toURLString(), {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let gunsInfos: any[] = response.data.data
        let guns: EVCBayOutletGun[] = []
        gunsInfos.forEach(gunInfo => {     
            
            let gun = new EVCBayOutletGunTransformer().transform(gunInfo)
            gun.setup = new EVCStationBaySetup(
                gunInfo.installationId, 
                station,
                new EVChargerTransformer().transform(gunInfo),
                [],
                new EVCStationBayStatus("", "")
            )
            let dummyTariff = new EVCBayOutletTariff("", "", new Date(), new Date())
            let dummyTariffs = [dummyTariff]            
            gun.outlet = new EVCBayOutlet("", "", new EVChargerOutputTransformer().transform(gunInfo), [], new EVChargeTariff(gunInfo.tariffCode, gunInfo.tariffCode), dummyTariffs,"","")
            gun.port = new EVChargerOutputPortTransformer().transform(gunInfo)
            gun.rate = new Rate(gunInfo.ratePerMinute, gunInfo.uom ? new UOM(gunInfo.uom, gunInfo.uom) : new UOM("minute", "/minute"), gunInfo.zeroTariff)
            gun.bayDetails = new BayDetailsForReservation(gunInfo.bayName, gunInfo.bayConnectorName);
            guns.push(gun)
        });
        
        // let thirdParty = new EVCStationThirdPartyList(response.data.meta.thirdPartyId, response.data.meta.thirdPartyName, response.data.meta.descriptions, response.data.meta.imgUrl)
        let chargerInfo = {
            gun: guns,
            thirdPartyInfo: response.data.meta
        }
        return Promise.resolve(chargerInfo)
    }

    async baySetupWithIdOfStation(id: number, station: EVCStation): Promise<EVCStationBaySetup> {
        let response = await axios.get(config.api.host + "/stations/"+ station.id + "/installations/" + id, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let baySetupInfo: any[] = response.data.data
        return Promise.resolve(new EVCStationBaySetupTransformer().transform(baySetupInfo))
    }

    async setupBayOnStationWithChargerByAuthor(baySetup: EVCStationBaySetup, station: EVCStation, author: User): Promise<void> {
        let baySetupInfo = new EVCStationBaySetupTransformer().deTransform(baySetup);
        
        await axios.post(config.api.host + "/stations/" + station.id + "/installations", {
            data: {                
                userId: author.id,
                ...baySetupInfo                
            } 
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        return Promise.resolve()
    }

    async reSetupBayOnStationWithChargerByAuthor(baySetup: EVCStationBaySetup, station: EVCStation, author: User): Promise<void> {
        let baySetupInfo = new EVCStationBaySetupTransformer().deTransform(baySetup)
        await axios.put(config.api.host + "/stations/" + station.id + "/installations/" + baySetup.id, {
            data: {                
                userId: author.id,
                ...baySetupInfo                
            } 
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        return Promise.resolve()
    }

    async saveSelectedChargerTypeOutputPortForUser(chargerType: EVChargerType, outputType: EVChargerOutputType, portType: EVChargerOutputPortType, user: User): Promise<void> {        
        let preferenceInfo = {
            chargerType: {
                code: chargerType.id
            },
            chargerOutput: {
                code: outputType.id
            },
            chargerPort: {
                code: portType.id
            }
        }
        await axios.post(config.api.host + "/accounts/"+ user.id + "/reservations/filters", {
            data: preferenceInfo
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        return Promise.resolve()
    }

    async clearSelectedChargerTypeOutputPortForUser(user: User): Promise<void> {
        await axios.post(config.api.host + "/accounts/"+ user.id + "/reservations/filters", {
            data: {
                
            }
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        return Promise.resolve()
    }

    async recentlySelectedFilterCriteriaForUser(user: User): Promise<RecentlySelectedFilterCriteria> {
        let response = await axios.get(config.api.host + "/accounts/"+ user.id + "/reservations/filters", {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let filterCriteriaInfo: any = response.data.data
        let filterCriteria = new RecentlySelectedFilterCriteria(
            new EVChargerType(filterCriteriaInfo.chargerType.code),                       
            {id: filterCriteriaInfo.chargerOutput.code} as any,
            {id: filterCriteriaInfo.chargerPort.code} as any
        )
        return Promise.resolve(filterCriteria)
    }

    async stationStatuses(): Promise<EVCStationStatus[]> {
        let response = await axios.get(config.api.host + "/stations/status", {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let statusesInfos: any[] = response.data.data
        let statuses: EVCStationStatus[] = []
        statusesInfos.forEach(statusInfo => {
            statuses.push(new EVCStationStatus(
                statusInfo.code,
                statusInfo.description
            ))
        });
        return Promise.resolve(statuses)
    }
    async stationVisibleStatuses(): Promise<EVCStationVisibiltyStatus[]> {
        let response = await axios.get(config.api.host + "/stations/chargingStationVisibleStatus", {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        
        let statusesInfos: any[] = response.data.data;
        let statuses: EVCStationVisibiltyStatus[] = []
        statusesInfos.forEach(statusInfo => {
            statuses.push(new EVCStationVisibiltyStatus(
                statusInfo.code,
                statusInfo.description
            ))
        });
        return Promise.resolve(statuses)
    }
    async thirdPartyStationStatus(): Promise<EVCStationThirdPartyStatus[]> {
        let response = await axios.get(config.api.host + "/stations/thirdPartyStationStatus", {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        
        let statusesInfos: any[] = response.data.data;
        let statuses: EVCStationThirdPartyStatus[] = []
        statusesInfos.forEach(statusInfo => {
            statuses.push(new EVCStationThirdPartyStatus(
                statusInfo.code,
                statusInfo.description
            ))
        });
        return Promise.resolve(statuses)
    }
    async thirdPartyStationList(): Promise<EVCStationThirdPartyList[]> {
        let response = await axios.get(config.api.host + "/stations/thirdPartyStationList", {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        
        let statusesInfos: any[] = response.data.data;
        let statuses: EVCStationThirdPartyList[] = []
        statusesInfos.forEach(statusInfo => {
            statuses.push(new EVCStationThirdPartyList(
                statusInfo.thirdPartyId,
                statusInfo.thirdPartyName,
                statusInfo.descriptions,
                statusInfo.imgUrl
            ))
        });
        return Promise.resolve(statuses)
    }
    async stationHolidays(station: EVCStation, user: User): Promise<EVCStationHoliday[]> {
        let response = await axios.get(config.api.host + "/accounts/" + user.id + "/reservations/stations/" + station.id + "/holidays", {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let holidaysInfos: any[] = response.data.data
        let holidays: EVCStationHoliday[] = []
        holidaysInfos.forEach(holidayInfo => {
            holidays.push(new EVCStationHoliday(
                moment(holidayInfo.date, moment.ISO_8601).toDate(),
                holidayInfo.reason,
                holidayInfo.timing
            ))
        });
        return Promise.resolve(holidays)
    }

    async stationImages(station: EVCStation, user: User): Promise<EVCStationImage[]> {
        let response = await axios.get(config.api.host + "/stations/" + station.id + "/photos", {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let imageInfo: any[] = response.data.data
        let images: EVCStationImage[] = []
        imageInfo.forEach(img => {
            images.push(new EVCStationImage(

                config.api.host + response.data.links + img
                
            ))
        });
        return Promise.resolve(images)
    }

    async installationStatuses(): Promise<EVCStationBayStatus[]> {
        let response = await axios.get(config.api.host + "/installations/status", {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let statusesInfos: any[] = response.data.data
        // let statusesInfos: any[] = [
        //     {
        //         code: "I",
        //         description: "INSTALLED"
        //     },
        //     {
        //         code: "P",
        //         description: "PENDING"
        //     }
        // ]
        let statuses: EVCStationBayStatus[] = []
        statusesInfos.forEach(statusInfo => {
            statuses.push(new EVCStationBayStatus(
                statusInfo.code,
                statusInfo.description
            ))
        });
        return Promise.resolve(statuses)
    }


    async baysAtPageByPaginatedWithSizeMatchingQuery(page: number, size: number, stationId?: string, query?: BayQuery): Promise<Result<EVCStationBaySetup>> {
        let baysQueryInfo: any = {
            page: page,
            "page-size": size,
            stationId: stationId
        }
        if(query) {
            if(query.station) {
                baysQueryInfo = {
                    ...baysQueryInfo,
                    station: {
                        stationName: query.station.name
                    }
                }
            }
            if(query.charger) {
                baysQueryInfo = {
                    ...baysQueryInfo,
                    charger: {
                        chargerId: query.charger.id
                    }
                }
            }
            if(query.chargerType) {
                baysQueryInfo = {
                    ...baysQueryInfo,
                    chargerType: {
                        code: query.chargerType.id
                    }
                }
            }
            if(query.effectiveBetween) {
                if(query.effectiveBetween.from) {
                    baysQueryInfo = { ...baysQueryInfo,
                        effectiveFrom: moment(query.effectiveBetween.from).utc().format('YYYY-MM-DD'),
                    }
                }
                if(query.effectiveBetween.to) {
                    baysQueryInfo = { ...baysQueryInfo,
                        effectiveTo: moment(query.effectiveBetween.to).utc().format('YYYY-MM-DD'),
                    }
                }
            }
            if(query.status) {
                baysQueryInfo = {
                    ...baysQueryInfo,
                    status: {
                        code: query.status.id
                    }
                }
            }
        }
        let response = await axios.get(config.api.host + "/installations/search" + new QueryInfo(baysQueryInfo).toURLString(), {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let baysInfos: any[] = response.data.data
        let baySetups: EVCStationBaySetup[] = []
        baysInfos.forEach(bayInfo => {
            baySetups.push(new EVCStationBaySetupTransformer().transform(bayInfo))
        });
        return Promise.resolve(new Result<EVCStationBaySetup>(response.data.meta.totalrecords, baySetups))
    }

    async createEVCStationByAuthor(station: EVCStation, user: User) : Promise<void> {
        let stationInfo = new StationTransformer().deTransform(station);    
        
        stationInfo = {
            userId: user.id,
            ...stationInfo
        }
        
        await axios.post(config.api.host + "/stations", {
            data: stationInfo
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        return Promise.resolve()
    }
    
    async updateEVCStationByAuthor(station: EVCStation, user: User) : Promise<void> {
        let stationInfo = new StationTransformer().deTransform(station);
        
        // const {stationId, ...stationInfoExcludingId} = stationInfo
        stationInfo = {
            userId: user.id,
            // ...stationInfoExcludingId        
            ...stationInfo
        }
        // 
        await axios.put(config.api.host + "/stations/" + station.id, {
            data: stationInfo
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        return Promise.resolve()
    }

    stationOperationHours(): HoursOfOperation[] {
        return [
            new HoursOfOperation(0, new Time(0, 0), new Time(23, 59)),
            new HoursOfOperation(1, new Time(0, 0), new Time(23, 59)),
            new HoursOfOperation(2, new Time(0, 0), new Time(23, 59)),
            new HoursOfOperation(3, new Time(0, 0), new Time(23, 59)),
            new HoursOfOperation(4, new Time(0, 0), new Time(23, 59)),
            new HoursOfOperation(5, new Time(0, 0), new Time(23, 59)),
            new HoursOfOperation(6, new Time(0, 0), new Time(23, 59)),
            new HoursOfOperation(7, new Time(0, 0), new Time(23, 59)),
        ]
    }
    //25.03.2021 qr code download service
    async downloadQRCodeFromSever(chargerId: any) {
        let qrData = {
          'chargerId': chargerId
        }
        let response = await axios.post(config.api.host + "/downloadQRCode", {
          data: qrData
        }, {
          headers: {
              Authorization: new LocalStorage().authToken
          }
      })
        return Promise.resolve(response)
  
    }

    async getStationInfoByQRCodeValue(qrCodeValue: string): Promise<any> {
        let response = await axios.get(config.api.host + "/stations/stationInfoByQRCode/" + qrCodeValue, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let stationInfo: any = response.data.data
        return Promise.resolve(stationInfo)
    }
}